import { useState, useEffect } from "react";
import { ExternalLink } from "lucide-react";
import { devWarn } from "@/lib/devLog";

const ARTIST_ID = "5emDOgnCiqhSYslFMzF734";
const EMBED_URL = `https://open.spotify.com/embed/artist/${ARTIST_ID}?utm_source=generator&theme=0`;
const ARTIST_URL = `https://open.spotify.com/artist/${ARTIST_ID}`;
const STORAGE_KEY = "aobe_spotify_open";

/**
 * Floating Spotify mini-player. The artist embed iframe is loaded by default
 * (online). A prominent "Listen on Spotify" button is always visible so users
 * can jump straight to the full app — and it also acts as a graceful fallback
 * if the embed itself ever fails to render.
 */
export default function SpotifyEmbed() {
  const [open, setOpen] = useState(() => {
    if (typeof window === "undefined") return true;
    return window.localStorage.getItem(STORAGE_KEY) !== "0";
  });

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, open ? "1" : "0");
    } catch (e) {
      devWarn("Spotify embed: persist failed", e);
    }
  }, [open]);

  return (
    <div
      data-testid="spotify-embed"
      className="fixed z-40 bottom-14 right-3 left-3 sm:left-auto sm:w-[360px] sm:bottom-12 pointer-events-auto"
    >
      <div className="rounded-xl border border-[var(--neon-red)]/40 bg-black/85 backdrop-blur-md shadow-[0_8px_32px_rgba(255,0,60,0.25)] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-white/10">
          <div className="flex items-center gap-2 min-w-0">
            <span className="w-2 h-2 rounded-full bg-[var(--neon-red)] animate-pulse" />
            <span className="font-hud text-[var(--crt-green)] text-base sm:text-lg truncate">
              NOW SPINNING — BLACK EMIGO
            </span>
          </div>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="font-hud text-sm text-white/80 hover:text-white px-2 py-0.5 border border-white/15 rounded shrink-0"
            data-testid="spotify-toggle"
            aria-label={open ? "Collapse player" : "Expand player"}
          >
            {open ? "—" : "+"}
          </button>
        </div>

        {/* Prominent "Listen on Spotify" CTA */}
        <div className="px-3 py-2 border-b border-white/10 bg-black/40">
          <a
            href={ARTIST_URL}
            target="_blank"
            rel="noreferrer noopener"
            data-testid="spotify-open-link"
            className="w-full inline-flex items-center justify-center gap-2 bg-[#1db954] hover:bg-[#1ed760] text-black font-body font-bold text-sm uppercase tracking-[0.16em] px-3 py-2 rounded-full transition-colors"
          >
            <SpotifyIcon className="w-4 h-4" />
            Listen on Spotify
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </div>

        {/* Live Spotify embed (default state: online) */}
        {open && (
          <div className="bg-black">
            <iframe
              title="Black Emigo on Spotify"
              src={EMBED_URL}
              width="100%"
              height="152"
              frameBorder="0"
              allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
              loading="lazy"
              style={{ border: 0, display: "block", background: "#000" }}
            />
          </div>
        )}
      </div>
    </div>
  );
}

function SpotifyIcon({ className = "w-4 h-4" }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      aria-hidden="true"
      fill="currentColor"
    >
      <path d="M12 0a12 12 0 1 0 0 24 12 12 0 0 0 0-24Zm5.5 17.34a.75.75 0 0 1-1.03.25c-2.82-1.72-6.38-2.11-10.56-1.15a.75.75 0 1 1-.33-1.46c4.55-1.04 8.46-.6 11.62 1.32.36.22.47.68.3 1.04Zm1.47-3.29a.93.93 0 0 1-1.29.31c-3.23-1.99-8.17-2.57-12-1.4a.94.94 0 1 1-.55-1.79c4.39-1.34 9.83-.69 13.55 1.59.43.27.57.84.29 1.29Zm.13-3.43c-3.88-2.3-10.27-2.51-13.97-1.39a1.12 1.12 0 1 1-.66-2.14c4.26-1.29 11.31-1.05 15.77 1.6a1.12 1.12 0 0 1-1.14 1.93Z" />
    </svg>
  );
}
